import { useCallback, useMemo } from "react"
import { useRouteMatch } from "react-router"
import moment, { Moment } from "moment"
import { endDate, startDate } from "utils"
import { PlanningRouteParams } from "app/components/PlanningView/PlanningNavigation/types"
import useUpdatePathParams from "hooks/useUpdatePathParams"
import { useGetDateByPlanningRoute } from "app/components/PlanningView/PlanningNavigation/hooks"
import { CalendarModes } from "./types"
import { updatePlanningPathParamsForDate } from "./utils"

interface IUsePlanningCalendar {
  date: Moment
  from: string
  to: string
  mode: CalendarModes
  setCalendarMode: (mode: CalendarModes) => void
  setDate: (date: Moment) => void
  setToday: () => void
}

const usePlanningCalendar = (): IUsePlanningCalendar => {
  const match = useRouteMatch<PlanningRouteParams>()
  const {
    params: { weekday },
  } = match
  const { updatePathParams } = useUpdatePathParams()
  const { date } = useGetDateByPlanningRoute()

  const mode = weekday ? CalendarModes.DAY : CalendarModes.WEEK
  const from = useMemo(() => startDate(date), [date])
  const to = useMemo(() => endDate(date), [date])

  const setCalendarMode = useCallback(
    (updatedMode: CalendarModes) => {
      updatePathParams(updatePlanningPathParamsForDate({ date, mode: updatedMode }))
    },
    [updatePathParams, date],
  )

  const setDate = useCallback(
    (updatedDate: Moment) => {
      updatePathParams(updatePlanningPathParamsForDate({ date: updatedDate, mode }))
    },
    [updatePathParams, mode],
  )

  const setToday = useCallback(() => {
    setDate(moment.utc())
  }, [setDate])

  return {
    date,
    mode,
    from,
    to,
    setCalendarMode,
    setDate,
    setToday,
  }
}

export default usePlanningCalendar
